import React, { useState, useEffect } from 'react';

interface PrivacyPolicySection {
  id: string;
  heading: string;
  content: string;
}

interface ContactInfo {
  heading: string;
  content: string;
  email: string;
  address: string;
  additionalInfo: string;
}

interface PrivacyPolicyData {
  title: string;
  introduction: string;
  sections: PrivacyPolicySection[];
  contactInfo: ContactInfo;
}

interface PrivacyPolicyContent {
  privacyPolicy: PrivacyPolicyData;
}

const PrivacyPolicy: React.FC = () => {
  const [content, setContent] = useState<PrivacyPolicyContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const loadPrivacyPolicy = async () => {
      try {
        const response = await fetch('/content/data/privacy-policy.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: PrivacyPolicyContent = await response.json();
        setContent(data);
      } catch (err) {
        console.error('Error loading privacy policy:', err);
        setError('Failed to load privacy policy content');
      } finally {
        setIsLoading(false);
      }
    };

    loadPrivacyPolicy();
  }, []);

  if (isLoading) {
    return (
      <div className="privacy-policy">
        <div className="container">
          <div className="loading">Loading privacy policy...</div>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="privacy-policy">
        <div className="container">
          <div className="error">
            <h1>Error</h1>
            <p>{error || 'Failed to load privacy policy'}</p>
          </div>
        </div>
      </div>
    );
  }

  const { privacyPolicy } = content;

  return (
    <div className="privacy-policy">
      <div className="container">
        <div className="privacy-policy__content">          <header className="privacy-policy__header">
            <h1 className="privacy-policy__title">{privacyPolicy.title}</h1>
          </header>

          <div className="privacy-policy__introduction">
            <p>{privacyPolicy.introduction}</p>
          </div>

          <div className="privacy-policy__sections">
            {privacyPolicy.sections.map((section) => (
              <section key={section.id} className="privacy-policy__section">
                <h2 className="privacy-policy__section-title">{section.heading}</h2>
                <p className="privacy-policy__section-content">{section.content}</p>
              </section>
            ))}
          </div>

          <div className="privacy-policy__contact">
            <h2 className="privacy-policy__section-title">{privacyPolicy.contactInfo.heading}</h2>
            <p className="privacy-policy__section-content">{privacyPolicy.contactInfo.content}</p>
            <div className="privacy-policy__contact-details">
              <p><strong>Email:</strong> <a href={`mailto:${privacyPolicy.contactInfo.email}`}>{privacyPolicy.contactInfo.email}</a></p>
              <p><strong>Address:</strong> {privacyPolicy.contactInfo.address}</p>
              <p>{privacyPolicy.contactInfo.additionalInfo}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
